/**
 * Conversation React Query Hooks
 *
 * Provides React Query hooks for conversation data fetching and mutations.
 * Includes proper cache invalidation.
 */

'use client'

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { conversationService } from '../api/services/conversations'
import type {
  Conversation,
  ConversationDetail,
  Message,
  CreateConversationRequest,
  UpdateConversationRequest,
  SendMessageRequest,
  StreamMessageRequest,
  StreamedMessageEvent,
  ConversationSearchParams,
} from '../api/types'

// ============================================
// Query Keys
// ============================================

export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (params?: ConversationSearchParams) => [...conversationKeys.lists(), params] as const,
  recent: (agentId?: string, limit?: number) =>
    [...conversationKeys.all, 'recent', agentId, limit] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string, skip?: number, take?: number) =>
    [...conversationKeys.details(), id, skip, take] as const,
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch conversations with optional filtering and pagination
 */
export function useConversations(
  params?: ConversationSearchParams,
  options?: Omit<UseQueryOptions<Conversation[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () => conversationService.getConversations(params),
    ...options,
  })
}

/**
 * Hook to fetch recent conversations for a specific agent
 */
export function useRecentConversations(
  agentId?: string,
  limit = 10,
  options?: Omit<UseQueryOptions<Conversation[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: conversationKeys.recent(agentId, limit),
    queryFn: () =>
      conversationService.getConversations({
        agentId,
        activeOnly: true,
        take: limit,
      }),
    ...options,
  })
}

/**
 * Hook to fetch a single conversation by ID with messages
 */
export function useConversation(
  id: string,
  options?: {
    skip?: number
    take?: number
    queryOptions?: Omit<UseQueryOptions<ConversationDetail, Error>, 'queryKey' | 'queryFn'>
  }
) {
  const skip = options?.skip ?? 0
  const take = options?.take ?? 50

  return useQuery({
    queryKey: conversationKeys.detail(id, skip, take),
    queryFn: () => conversationService.getConversation(id, skip, take),
    enabled: !!id,
    ...options?.queryOptions,
  })
}

/**
 * Hook to fetch messages for a conversation (alias for useConversation returning messages)
 */
export function useMessages(
  conversationId: string,
  options?: {
    skip?: number
    take?: number
    queryOptions?: Omit<UseQueryOptions<Message[], Error>, 'queryKey' | 'queryFn'>
  }
) {
  const skip = options?.skip ?? 0
  const take = options?.take ?? 50

  return useQuery({
    queryKey: conversationKeys.detail(conversationId, skip, take),
    queryFn: async () => {
      const conversation = await conversationService.getConversation(conversationId, skip, take)
      return conversation.messages
    },
    enabled: !!conversationId,
    ...options?.queryOptions,
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new conversation
 */
export function useCreateConversation(
  options?: UseMutationOptions<Conversation, Error, CreateConversationRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateConversationRequest) => conversationService.createConversation(data),
    onSuccess: () => {
      // Invalidate conversation list queries to refetch
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to update a conversation
 */
export function useUpdateConversation(
  options?: UseMutationOptions<Conversation, Error, { id: string; data: UpdateConversationRequest }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => conversationService.updateConversation(id, data),
    onSuccess: (updatedConversation, variables) => {
      // Update the conversation in cache
      queryClient.setQueryData(conversationKeys.detail(variables.id), (old: ConversationDetail | undefined) =>
        old ? { ...old, ...updatedConversation } : undefined
      )

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to delete (archive) a conversation
 */
export function useDeleteConversation(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => conversationService.archiveConversation(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: conversationKeys.detail(id) })

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to archive a conversation
 */
export function useArchiveConversation(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => conversationService.archiveConversation(id),
    onSuccess: (_data, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: conversationKeys.detail(id) })

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to complete a conversation
 */
export function useCompleteConversation(
  options?: UseMutationOptions<Conversation, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => conversationService.completeConversation(id),
    onSuccess: (completedConversation, id) => {
      // Update the conversation in cache
      queryClient.setQueryData(conversationKeys.detail(id), (old: ConversationDetail | undefined) =>
        old ? { ...old, ...completedConversation } : undefined
      )

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to send a message (non-streaming)
 */
export function useSendMessage(
  options?: UseMutationOptions<Message, Error, SendMessageRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendMessageRequest) => conversationService.sendMessage(data),
    onSuccess: (_response, variables) => {
      // Invalidate conversation detail to refetch messages
      queryClient.invalidateQueries({
        queryKey: conversationKeys.details(),
        predicate: (query) => {
          const key = query.queryKey
          return key[2] === variables.conversationId
        },
      })

      // Invalidate conversation lists (message count changed)
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
    ...options,
  })
}

/**
 * Hook to get streaming message callbacks
 * Note: Streaming doesn't use React Query mutations directly,
 * but this hook provides properly-typed cache invalidation callbacks.
 */
export function useStreamMessageCallbacks() {
  const queryClient = useQueryClient()

  return {
    /**
     * Start streaming a message
     * @param conversationId The conversation ID
     * @param data The message request data
     * @param onEvent Callback for each stream event
     * @param onError Optional error callback
     * @returns A cleanup function to abort the stream
     */
    streamMessage: (
      conversationId: string,
      data: StreamMessageRequest,
      onEvent: (event: StreamedMessageEvent) => void,
      onError?: (error: Error) => void
    ) => {
      return conversationService.streamMessage(conversationId, data, onEvent, onError)
    },

    /**
     * Call this when a streaming message completes successfully
     */
    onStreamComplete: (conversationId: string) => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.details(),
        predicate: (query) => {
          const key = query.queryKey
          return key[2] === conversationId
        },
      })
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },

    /**
     * Manually invalidate conversation caches
     */
    invalidateConversation: (conversationId: string) => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.details(),
        predicate: (query) => {
          const key = query.queryKey
          return key[2] === conversationId
        },
      })
    },
  }
}
